import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  ArrowLeft, Calendar, TrendingUp, TrendingDown, DollarSign,
  CheckCircle2, Clock, User, FolderKanban, Plus, Edit2, Trash2, Target,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import MasonryInventorySection from '../components/MasonryInventorySection';
import WomenDevelopmentSection from '../components/WomenDevelopmentSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  planning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  on_hold: 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400',
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const { hasRole } = useAuth();
  const canEditMilestones = hasRole(['admin', 'project_manager']);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msDialog, setMsDialog] = useState(false);
  const [editingMs, setEditingMs] = useState(null);
  const [msForm, setMsForm] = useState({
    title: '', description: '', due_date: '', status: 'pending',
  });

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchDetail = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/${projectId}/detail`);
      setDetail(res.data);
    } catch (e) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const openCreateMs = () => {
    setEditingMs(null);
    setMsForm({ title: '', description: '', due_date: '', status: 'pending' });
    setMsDialog(true);
  };

  const openEditMs = (m) => {
    setEditingMs(m);
    setMsForm({
      title: m.title || '',
      description: m.description || '',
      due_date: m.due_date || '',
      status: m.status || 'pending',
    });
    setMsDialog(true);
  };

  const submitMs = async (e) => {
    e?.preventDefault();
    if (!msForm.title || !msForm.due_date) {
      toast.error('Title and due date are required');
      return;
    }
    try {
      const payload = { ...msForm, project_id: projectId };
      if (editingMs) {
        await axios.put(`${API_URL}/api/milestones/${editingMs.id}`, payload);
        toast.success('Milestone updated');
      } else {
        await axios.post(`${API_URL}/api/milestones`, payload);
        toast.success('Milestone created');
      }
      setMsDialog(false);
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const toggleMsStatus = async (m) => {
    const newStatus = m.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API_URL}/api/milestones/${m.id}`, {
        title: m.title,
        description: m.description || '',
        due_date: m.due_date,
        status: newStatus,
        project_id: projectId,
      });
      toast.success(newStatus === 'completed' ? 'Marked complete' : 'Reopened');
      fetchDetail();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteMs = async (id) => {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      await axios.delete(`${API_URL}/api/milestones/${id}`);
      toast.success('Deleted');
      fetchDetail();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!detail) {
    return (
      <Card><CardContent className="p-12 text-center text-muted-foreground">Project not found</CardContent></Card>
    );
  }

  const { project, milestones, income, expenses, production, revenue, total_expenses, net_income } = detail;
  const completedMs = milestones.filter(m => m.status === 'completed').length;
  const progress = milestones.length > 0 ? (completedMs / milestones.length) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div className="flex items-center gap-2">
        <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Link>
      </div>

      {/* Header */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-primary/70 p-8 text-white">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <FolderKanban className="h-8 w-8" />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="font-heading text-3xl font-bold">{project.name}</h1>
                  <p className="text-white/80 capitalize mt-1">{project.project_type?.replace(/_/g, ' ')}</p>
                </div>
                <Badge className={STATUS_STYLES[project.status] || 'bg-white/20'}>{project.status}</Badge>
              </div>
              {project.description && (
                <p className="mt-4 text-white/90 leading-relaxed max-w-3xl">{project.description}</p>
              )}
            </div>
          </div>
        </div>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted"><Calendar className="h-4 w-4" /></div>
              <div>
                <p className="text-muted-foreground">Timeline</p>
                <p className="font-medium">
                  {formatDate(project.start_date)}
                  {project.end_date ? ` → ${formatDate(project.end_date)}` : ' → Ongoing'}
                </p>
              </div>
            </div>
            {project.manager_name && (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted"><User className="h-4 w-4" /></div>
                <div>
                  <p className="text-muted-foreground">Manager</p>
                  <p className="font-medium">{project.manager_name}</p>
                </div>
              </div>
            )}
            {project.budget > 0 && (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted"><Target className="h-4 w-4" /></div>
                <div>
                  <p className="text-muted-foreground">Budget</p>
                  <p className="font-medium">{formatCurrency(project.budget)}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Financial summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="project-financial-cards">
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/20">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Revenue</p>
              <p className="text-2xl font-heading font-bold text-green-600">{formatCurrency(revenue)}</p>
              <p className="text-xs text-muted-foreground">{income.length} entries</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/20">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Expenses</p>
              <p className="text-2xl font-heading font-bold text-red-600">{formatCurrency(total_expenses)}</p>
              <p className="text-xs text-muted-foreground">{expenses.length} entries</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`card-hover ${net_income >= 0 ? 'bg-primary/5 border-primary/20' : 'bg-red-50 dark:bg-red-900/10 border-red-200'}`}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${net_income >= 0 ? 'bg-primary/10' : 'bg-red-100 dark:bg-red-900/20'}`}>
              <DollarSign className={`h-6 w-6 ${net_income >= 0 ? 'text-primary' : 'text-red-600'}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Income</p>
              <p className={`text-2xl font-heading font-bold ${net_income >= 0 ? 'text-primary' : 'text-red-600'}`}>
                {formatCurrency(net_income)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Milestones / Timeline */}
      <Card data-testid="milestones-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="h-5 w-5" /> Milestones & Timeline
              </CardTitle>
              <CardDescription>
                {milestones.length === 0
                  ? 'No milestones yet'
                  : `${completedMs} of ${milestones.length} completed`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {milestones.length > 0 && (
                <div className="w-40">
                  <Progress value={progress} />
                  <p className="text-xs text-muted-foreground text-right mt-1">{progress.toFixed(0)}%</p>
                </div>
              )}
              {canEditMilestones && (
                <Button size="sm" onClick={openCreateMs} data-testid="add-milestone-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Milestone
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {milestones.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {canEditMilestones ? 'Click "Add Milestone" to plan the first one.' : 'No milestones planned yet.'}
            </div>
          ) : (
            <div className="space-y-3">
              {milestones.map((m) => (
                <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50" data-testid={`milestone-${m.id}`}>
                  <button
                    type="button"
                    disabled={!canEditMilestones}
                    onClick={() => toggleMsStatus(m)}
                    className={`p-1.5 rounded-full transition-colors ${
                      m.status === 'completed'
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                    } ${!canEditMilestones && 'cursor-default'}`}
                    title={canEditMilestones ? 'Toggle completed' : ''}
                    data-testid={`toggle-milestone-${m.id}`}
                  >
                    {m.status === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  </button>
                  <div className="flex-1">
                    <p className="font-medium">{m.title}</p>
                    {m.description && <p className="text-sm text-muted-foreground">{m.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1">Due: {formatDate(m.due_date)}</p>
                  </div>
                  <Badge variant="outline" className="capitalize">{m.status}</Badge>
                  {canEditMilestones && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditMs(m)} data-testid={`edit-milestone-${m.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteMs(m.id)} data-testid={`delete-milestone-${m.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Milestone Dialog */}
      <Dialog open={msDialog} onOpenChange={setMsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMs ? 'Edit Milestone' : 'Add Milestone'}</DialogTitle>
            <DialogDescription>Plan a key project deliverable with a due date.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMs} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title *</Label>
              <Input
                value={msForm.title}
                onChange={(e) => setMsForm({ ...msForm, title: e.target.value })}
                required
                data-testid="milestone-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={msForm.description}
                onChange={(e) => setMsForm({ ...msForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={msForm.due_date}
                  onChange={(e) => setMsForm({ ...msForm, due_date: e.target.value })}
                  required
                  data-testid="milestone-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={msForm.status} onValueChange={(v) => setMsForm({ ...msForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMsDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-milestone-btn">
                {editingMs ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project-type specific sections */}
      {project.project_type === 'masonry' && <MasonryInventorySection />}
      {project.project_type === 'women_development' && <WomenDevelopmentSection />}

      {/* Tabs for Income / Expenses / Production */}
      <Tabs defaultValue="income">
        <TabsList>
          <TabsTrigger value="income">Income ({income.length})</TabsTrigger>
          <TabsTrigger value="expenses">Expenses ({expenses.length})</TabsTrigger>
          {production.length > 0 && <TabsTrigger value="production">Production ({production.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="income" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {income.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No income entries linked</TableCell></TableRow>
                  ) : (
                    income.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell>{formatDate(i.date)}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{i.source?.replace(/_/g, ' ')}</Badge></TableCell>
                        <TableCell className="text-muted-foreground">{i.description || '-'}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{formatCurrency(i.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No expense entries linked</TableCell></TableRow>
                  ) : (
                    expenses.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{formatDate(e.date)}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{e.category?.replace(/_/g, ' ')}</Badge></TableCell>
                        <TableCell className="text-muted-foreground">{e.description || '-'}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">{formatCurrency(e.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {production.length > 0 && (
          <TabsContent value="production" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {production.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{formatDate(p.date)}</TableCell>
                        <TableCell>{p.product_name || p.product_type}</TableCell>
                        <TableCell>{p.quantity}</TableCell>
                        <TableCell>{p.unit || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{p.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ProjectDetailPage;
